import { Button, CircularProgress } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import { FlockProfileTestModel } from 'api/query/flock/models';
import { FlockProfileCardModel } from 'components/flock-profile-card';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { LogosEnum } from 'pages/report/export-pdf/pdf-logo/logos-enum';
import React, { useState } from 'react';

type FlockProfileDetailPdfProps = {
  cardModel: FlockProfileCardModel;
  report: FlockProfileTestModel;
  reportId: number;
  selectedIndexLabel?: string;
  targetId: string;
};

type FontReadyDocument = Document & {
  fonts?: {
    ready: Promise<unknown>;
  };
};

type JsPdfWithPageCount = jsPDF & {
  getNumberOfPages: () => number;
};

const pageOverflowTolerancePx = 32;
const metadataTextGrey = 75;
const pdfChartContentLeftX = 10;
const pdfChartSourceLeftPaddingPx = 0;

const formatDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-GB');
};

const getFileSafeValue = (value: string): string => (
  value.trim().replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
);

const waitForFonts = async () => {
  const fontReadyDocument = document as FontReadyDocument;

  if (fontReadyDocument.fonts) {
    await fontReadyDocument.fonts.ready;
  }
};

const addFullPageHeader = (
  pdf: jsPDF,
  report: FlockProfileTestModel,
  reportId: number,
  selectedIndexLabel?: string,
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.addImage(LogosEnum.sg, 'JPEG', 127, 12, 47.3, 7.2, 'SheepGenetics-logo', 'FAST');
  pdf.addImage(LogosEnum.mla, 'JPEG', pageWidth - 30, 10, 20, 9.2, 'MLA-logo', 'FAST');

  pdf.setFontSize(8);
  pdf.setTextColor(metadataTextGrey);
  const metadataColumns = [
    [
      { label: 'Report', value: 'Flock Profile' },
      { label: 'Order ID', value: report.orderId || '-' },
      { label: 'Drop year', value: report.dropYearLabel || '-' },
      { label: 'Profile ID', value: reportId.toString() },
    ],
    [
      { label: 'Company', value: report.breedFlockName || '-' },
      { label: 'Index', value: selectedIndexLabel || '-' },
      { label: 'Analysis', value: report.analysisType || '-' },
      { label: 'Analysis date', value: formatDate(report.analysisDate) },
    ],
  ];
  const columns = [
    { labelX: 10, valueX: 30, valueWidth: 18 },
    { labelX: 55, valueX: 79, valueWidth: 28 },
  ];

  metadataColumns.forEach((columnRows, columnIndex) => {
    const column = columns[columnIndex];

    columnRows.forEach((item, rowIndex) => {
      const y = 9 + (rowIndex * 5);

      pdf.setFont(undefined, 'bold');
      pdf.text(`${item.label}:`, column.labelX, y);
      pdf.setFont(undefined, 'normal');
      pdf.text(pdf.splitTextToSize(item.value, column.valueWidth)[0], column.valueX, y);
    });
  });
  pdf.setTextColor(0);
};

const addMinimalPageHeader = (pdf: jsPDF) => {
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.addImage(LogosEnum.sg, 'JPEG', 10, 10, 47.3, 7.2, 'SheepGenetics-logo-minimal', 'FAST');
  pdf.addImage(LogosEnum.mla, 'JPEG', pageWidth - 30, 9, 20, 9.2, 'MLA-logo-minimal', 'FAST');
};

const addPageFooter = (pdf: jsPDF, pageNumber: number, pageCount: number) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const generatedDate = new Date().toLocaleDateString('en-GB');

  pdf.setFontSize(8);
  pdf.setTextColor(110);
  pdf.text(generatedDate, 10, pageHeight - 8);
  pdf.text(`Page ${pageNumber} of ${pageCount}`, pageWidth - 10, pageHeight - 8, { align: 'right' });
  pdf.setTextColor(0);
};

const addFooters = (pdf: jsPDF) => {
  const pageCount = (pdf as JsPdfWithPageCount).getNumberOfPages();

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    pdf.setPage(pageNumber);
    addPageFooter(pdf, pageNumber, pageCount);
  }
};

const renderElementToCanvas = async (element: HTMLElement): Promise<HTMLCanvasElement> => html2canvas(element, {
  scrollY: -window.scrollY,
  scale: 3,
  useCORS: true,
  backgroundColor: '#ffffff',
});

const getCanvasPageCount = (canvasHeight: number, sliceHeight: number): number => {
  const pageCount = Math.max(1, Math.ceil(canvasHeight / sliceHeight));
  const trailingSliceHeight = canvasHeight % sliceHeight;

  if (pageCount > 1 && trailingSliceHeight > 0 && trailingSliceHeight <= pageOverflowTolerancePx) {
    return pageCount - 1;
  }

  return pageCount;
};

const addSinglePageCanvasToPdf = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  report: FlockProfileTestModel,
  reportId: number,
  selectedIndexLabel: string | undefined,
  headerHeight: number,
  footerHeight: number,
  marginX: number,
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - (marginX * 2);
  const contentHeight = pageHeight - headerHeight - footerHeight;
  const imageHeightAtFullWidth = (canvas.height * contentWidth) / canvas.width;
  const imageWidth = imageHeightAtFullWidth > contentHeight
    ? (contentHeight * canvas.width) / canvas.height
    : contentWidth;
  const imageHeight = imageHeightAtFullWidth > contentHeight
    ? contentHeight
    : imageHeightAtFullWidth;

  addFullPageHeader(pdf, report, reportId, selectedIndexLabel);
  pdf.addImage(
    canvas.toDataURL('image/png', 1.0),
    'PNG',
    Math.max(marginX, pdfChartContentLeftX - ((imageWidth * pdfChartSourceLeftPaddingPx) / canvas.width)),
    headerHeight,
    imageWidth,
    imageHeight,
    undefined,
    'FAST',
  );
};

const addSectionCanvasToPdf = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  cursorY: number,
  headerHeight: number,
  footerHeight: number,
  marginX: number,
): number => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - (marginX * 2);
  const contentHeight = pageHeight - headerHeight - footerHeight;
  const imageHeight = (canvas.height * contentWidth) / canvas.width;

  if (cursorY > headerHeight && cursorY + imageHeight > pageHeight - footerHeight) {
    pdf.addPage();
    addMinimalPageHeader(pdf);
    cursorY = headerHeight;
  }

  if (imageHeight <= contentHeight) {
    pdf.addImage(
      canvas.toDataURL('image/png', 1.0),
      'PNG',
      marginX,
      cursorY,
      contentWidth,
      imageHeight,
      undefined,
      'FAST',
    );

    return cursorY + imageHeight;
  }

  const sliceHeight = (contentHeight * canvas.width) / contentWidth;
  const pageCount = getCanvasPageCount(canvas.height, sliceHeight);

  // TODO: If SG supplies notice/help text longer than a page, replace this canvas slice fallback with paragraph-aware rendering.
  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    if (pageIndex > 0 || cursorY > headerHeight) {
      pdf.addPage();
      addMinimalPageHeader(pdf);
      cursorY = headerHeight;
    }

    const sourceY = pageIndex * sliceHeight;
    const currentSliceHeight = Math.min(sliceHeight, canvas.height - sourceY);
    const pageCanvas = document.createElement('canvas');
    const pageContext = pageCanvas.getContext('2d');

    pageCanvas.width = canvas.width;
    pageCanvas.height = currentSliceHeight;

    if (pageContext) {
      pageContext.fillStyle = '#ffffff';
      pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageContext.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        currentSliceHeight,
        0,
        0,
        pageCanvas.width,
        pageCanvas.height,
      );
    }

    pdf.addImage(
      pageCanvas.toDataURL('image/png', 1.0),
      'PNG',
      marginX,
      cursorY,
      contentWidth,
      (currentSliceHeight * contentWidth) / canvas.width,
      undefined,
      'FAST',
    );
  }

  return headerHeight + ((canvas.height % sliceHeight || sliceHeight) * contentWidth) / canvas.width;
};

export const FlockProfileDetailPdf: React.FC<FlockProfileDetailPdfProps> = ({
  cardModel,
  report,
  reportId,
  selectedIndexLabel,
  targetId,
}) => {
  const [generating, setGenerating] = useState(false);

  const generatePdf = async () => {
    const target = document.getElementById(targetId);

    if (!target) {
      return;
    }

    setGenerating(true);
    const previousScrollY = window.scrollY;
    window.scrollTo(0, 0);

    try {
      await waitForFonts();
      const pdf = new jsPDF('portrait');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 6;
      const headerHeight = 27;
      const minimalHeaderHeight = 18;
      const footerHeight = 10;
      const contentWidth = pageWidth - (marginX * 2);
      const contentHeight = pageHeight - headerHeight - footerHeight;
      const chartElement = target.querySelector('.sg-flock-profile-card') as HTMLElement | null;
      const sectionsElement = target.querySelector('.sg-flock-profile-report__pdf-sections') as HTMLElement | null;

      if (chartElement && sectionsElement) {
        addSinglePageCanvasToPdf(
          pdf,
          await renderElementToCanvas(chartElement),
          report,
          reportId,
          selectedIndexLabel,
          headerHeight,
          footerHeight,
          marginX,
        );

        pdf.addPage();
        addMinimalPageHeader(pdf);
        addSectionCanvasToPdf(
          pdf,
          await renderElementToCanvas(sectionsElement),
          minimalHeaderHeight,
          minimalHeaderHeight,
          footerHeight,
          marginX,
        );
      } else {
        const canvas = await renderElementToCanvas(target);
        const sliceHeight = (contentHeight * canvas.width) / contentWidth;
        const pageCount = getCanvasPageCount(canvas.height, sliceHeight);

        for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
          const sourceY = pageIndex * sliceHeight;
          const currentSliceHeight = Math.min(sliceHeight, canvas.height - sourceY);
          const pageCanvas = document.createElement('canvas');
          const pageContext = pageCanvas.getContext('2d');

          if (pageIndex > 0) {
            pdf.addPage();
          }

          pageCanvas.width = canvas.width;
          pageCanvas.height = currentSliceHeight;

          if (pageContext) {
            pageContext.fillStyle = '#ffffff';
            pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            pageContext.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              currentSliceHeight,
              0,
              0,
              pageCanvas.width,
              pageCanvas.height,
            );
          }

          addFullPageHeader(pdf, report, reportId, selectedIndexLabel);
          pdf.addImage(
            pageCanvas.toDataURL('image/png', 1.0),
            'PNG',
            marginX,
            headerHeight,
            contentWidth,
            (currentSliceHeight * contentWidth) / canvas.width,
            undefined,
            'FAST',
          );
        }
      }

      addFooters(pdf);

      const fileName = [
        'flock-profile',
        reportId.toString(),
        getFileSafeValue(cardModel.flockCode),
        getFileSafeValue(cardModel.analysisName),
      ].filter((value) => value.length > 0).join('-');

      pdf.save(`${fileName}.pdf`);
    } finally {
      window.scrollTo(0, previousScrollY);
      setGenerating(false);
    }
  };

  return (
    <Button
      type='button'
      variant='contained'
      color='primary'
      startIcon={generating ? <CircularProgress color="inherit" size={20} /> : <GetApp />}
      disabled={generating}
      onClick={generatePdf}
    >
      {generating ? 'Generating PDF...' : 'Download PDF'}
    </Button>
  );
};

export default FlockProfileDetailPdf;
